import React from 'react';
import styled from 'styled-components';
import { KpiSummary } from '../../types/drilldown';
import { Colors, Spacing, FontSizes, BorderRadius } from '../../constants/theme';

interface DrillDownSummaryProps {
  summary: KpiSummary;
}

const SummaryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xxxl}px;
  padding: ${Spacing.xl}px;
  background: ${Colors.backgroundLight};
  border-radius: ${BorderRadius.md}px;
  border: 1px solid ${Colors.borderLight};
`;

const SummaryCard = styled.div`
  text-align: center;
  padding: ${Spacing.md}px;
`;

const SummaryLabel = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${Spacing.xs}px;
  font-weight: 500;
`;

const SummaryValue = styled.div<{ status?: 'good' | 'warning' | 'critical' }>`
  font-size: ${FontSizes.xxxl}px;
  font-weight: 800;
  color: ${props => {
    switch (props.status) {
      case 'good': return Colors.success;
      case 'warning': return Colors.warning;
      case 'critical': return Colors.danger;
      default: return Colors.textPrimary;
    }
  }};
  margin-bottom: ${Spacing.xs}px;
  line-height: 1.1;
`;

const SummaryUnit = styled.span`
  font-size: ${FontSizes.lg}px;
  color: ${Colors.textSecondary};
  font-weight: 400;
  margin-left: ${Spacing.xs}px;
`;

const ChangeIndicator = styled.div<{ changeType: 'increase' | 'decrease' | 'neutral' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs}px;
  font-size: ${FontSizes.sm}px;
  font-weight: 600;
  color: ${props => {
    switch (props.changeType) {
      case 'increase': return Colors.success;
      case 'decrease': return Colors.danger;
      case 'neutral': return Colors.textSecondary;
      default: return Colors.textSecondary;
    }
  }};
`;

const ChangeIcon = styled.span<{ changeType: 'increase' | 'decrease' | 'neutral' }>`
  font-size: ${FontSizes.md}px;
`;

const BenchmarkContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs}px;
  margin-top: ${Spacing.sm}px;
`;

const BenchmarkItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
`;

const BenchmarkValue = styled.span`
  font-weight: 600;
  color: ${Colors.textSecondary};
`;

const StatusIndicator = styled.div<{ status: 'good' | 'warning' | 'critical' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  font-size: ${FontSizes.xs}px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => {
    switch (props.status) {
      case 'good': return Colors.success;
      case 'warning': return Colors.warning;
      case 'critical': return Colors.danger;
      default: return Colors.textSecondary;
    }
  }};
`;

const StatusDot = styled.div<{ status: 'good' | 'warning' | 'critical' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    switch (props.status) {
      case 'good': return Colors.success;
      case 'warning': return Colors.warning;
      case 'critical': return Colors.danger;
      default: return Colors.textTertiary;
    }
  }};
`;

export const DrillDownSummary: React.FC<DrillDownSummaryProps> = ({ summary }) => {
  const formatValue = (value: number, unit: string) => {
    if (unit === '%') {
      return value.toFixed(1);
    }
    
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    }
    
    if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'K';
    }
    
    return value.toLocaleString();
  };

  const getChangeIcon = (changeType: 'increase' | 'decrease' | 'neutral') => {
    switch (changeType) {
      case 'increase': return '↗';
      case 'decrease': return '↘';
      case 'neutral': return '→';
      default: return '→';
    }
  };

  const getStatusText = (status: 'good' | 'warning' | 'critical') => {
    switch (status) {
      case 'good': return 'On Track';
      case 'warning': return 'At Risk';
      case 'critical': return 'Critical';
      default: return 'Unknown';
    }
  };

  return (
    <SummaryContainer>
      {/* Main Value */}
      <SummaryCard>
        <SummaryLabel>Current Value</SummaryLabel>
        <SummaryValue status={summary.status}>
          {formatValue(summary.value, summary.unit)}
          <SummaryUnit>{summary.unit}</SummaryUnit>
        </SummaryValue>
        <StatusIndicator status={summary.status}>
          <StatusDot status={summary.status} />
          {getStatusText(summary.status)}
        </StatusIndicator>
      </SummaryCard>

      {/* Change */}
      {summary.change !== undefined && summary.changeType && (
        <SummaryCard>
          <SummaryLabel>Change</SummaryLabel>
          <ChangeIndicator changeType={summary.changeType}>
            <ChangeIcon changeType={summary.changeType}>
              {getChangeIcon(summary.changeType)}
            </ChangeIcon>
            {Math.abs(summary.change).toFixed(1)}{summary.unit === '%' ? 'pp' : summary.unit}
          </ChangeIndicator>
          <BenchmarkContainer>
            <BenchmarkItem>
              <span>vs Previous Period</span>
            </BenchmarkItem>
          </BenchmarkContainer>
        </SummaryCard>
      )}

      {/* Target */}
      {summary.target !== undefined && (
        <SummaryCard>
          <SummaryLabel>Target</SummaryLabel>
          <SummaryValue>
            {formatValue(summary.target, summary.unit)}
            <SummaryUnit>{summary.unit}</SummaryUnit>
          </SummaryValue>
          <BenchmarkContainer>
            <BenchmarkItem>
              <span>Progress:</span>
              <BenchmarkValue>
                {((summary.value / summary.target) * 100).toFixed(0)}%
              </BenchmarkValue>
            </BenchmarkItem>
          </BenchmarkContainer>
        </SummaryCard>
      )}

      {/* Benchmark */}
      {summary.benchmark !== undefined && (
        <SummaryCard>
          <SummaryLabel>Benchmark</SummaryLabel>
          <SummaryValue>
            {formatValue(summary.benchmark, summary.unit)}
            <SummaryUnit>{summary.unit}</SummaryUnit>
          </SummaryValue>
          <BenchmarkContainer>
            <BenchmarkItem>
              <span>vs Industry:</span>
              <BenchmarkValue>
                {summary.value > summary.benchmark ? '+' : ''}
                {((summary.value - summary.benchmark) / summary.benchmark * 100).toFixed(1)}%
              </BenchmarkValue>
            </BenchmarkItem>
          </BenchmarkContainer>
        </SummaryCard>
      )}
    </SummaryContainer>
  );
};